import {type ActionType as TSActionType, getType} from 'typesafe-actions';

import * as InteractStateActions from './actions';
import {
  type Highlight,
  type HighlightUpdate,
  type InteractState,
} from './types';

export const EMPTY_STATE: InteractState = {
  highlight: {},
  panelSelection: [],
};

function updateHighlight(
  highlight: Highlight,
  {axis, value, metric}: HighlightUpdate
): Highlight {
  if (value == null) {
    const hadAxis = axis in highlight;
    const hadMetric = axis === 'run:name' && 'run:metric' in highlight;
    if (!hadAxis && !hadMetric) {
      // Nothing to clear: return the same reference so a no-op dispatch does
      // not allocate or force subscribers to re-render.
      return highlight;
    }

    const newHighlight = {...highlight};
    delete newHighlight[axis];
    // Clear metric when clearing run:name
    if (axis === 'run:name') {
      delete newHighlight['run:metric'];
    }
    return newHighlight;
  }

  const valueUnchanged = highlight[axis] === value;
  let metricUnchanged = true;
  if (axis === 'run:name') {
    if (metric != null) {
      metricUnchanged = highlight['run:metric'] === metric;
    } else {
      metricUnchanged = !('run:metric' in highlight);
    }
  }
  if (valueUnchanged && metricUnchanged) {
    // Same value (and metric, for run:name) is already set: no-op, keep ref.
    return highlight;
  }

  const newHighlight = {...highlight};
  newHighlight[axis] = value;
  // Store/clear metric alongside run:name
  if (axis === 'run:name') {
    if (metric != null) {
      newHighlight['run:metric'] = metric;
    } else {
      delete newHighlight['run:metric'];
    }
  }
  return newHighlight;
}

export default function reducer(
  state: InteractState = EMPTY_STATE,
  action: TSActionType<typeof InteractStateActions>
) {
  switch (action.type) {
    case getType(InteractStateActions.setHighlight): {
      const highlight = updateHighlight(state.highlight, action.payload);
      if (highlight === state.highlight) {
        return state;
      }
      return {...state, highlight};
    }
    case getType(InteractStateActions.setHighlights): {
      const {highlights} = action.payload;
      const highlight = highlights.reduce(updateHighlight, state.highlight);
      if (highlight === state.highlight) {
        return state;
      }
      return {...state, highlight};
    }
    case getType(InteractStateActions.setPanelSelection): {
      return {
        ...state,
        panelSelection: action.payload.value,
      };
    }
    default: {
      return state;
    }
  }
}
